import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPatch } from "../lib/api";
import { useGoals, queryKeys } from "../lib/queries";
import { LoadingState, ErrorState } from "./Loading";

export interface GoalRow {
  id: string;
  client_id: string;
  text: string;
  added_by_role: "coach" | "guardian" | "athlete";
  created_at: string;
  target_date: string | null;
  achieved_at: string | null;
}

/** For `achieved_at`, a genuine instant -- local-time display is correct here. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

/**
 * For `target_date`, a calendar date rather than an instant. `new Date("2027-03-01")` parses as UTC
 * midnight, so formatting it with `toLocaleDateString()` in any timezone behind UTC (all of the US, for
 * one) prints the day *before* the one the coach typed -- the same wrong-direction date-shift class of
 * bug as the weight-unit mislabelling this same debug session already fixed. Building the `Date` from
 * the local year/month/day components instead of parsing the string sidesteps the UTC round-trip
 * entirely, so this can't drift regardless of which timezone it renders in.
 */
export function formatTargetDate(dateOnly: string): string {
  const [year, month, day] = dateOnly.slice(0, 10).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime())
    ? dateOnly
    : date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

/**
 * A client's goals (27 Sep debug notes item 8).
 *
 * A goal used to hang off whichever plan was live when it was set ("text", "checked"), so "Land a double
 * axel" set against August's plan vanished from view the moment September's plan was published, achieving
 * one was a bare tickbox with no memory of when, and there was no coach screen for goals at all --
 * "coach cannot edit goals" turned out to be a missing UI, not a missing permission; the backend already
 * let the coach write the old plan-scoped ones.
 *
 * Used by both the athlete/guardian's own walkthrough and the coach's client page, so the two sides of a
 * goal (set it, work it, mark it) stay in exactly one place rather than drifting apart.
 */
export function ClientGoals({ clientId, canWrite }: { clientId: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const goalsQuery = useGoals<GoalRow[]>(clientId);
  const goals = goalsQuery.data ?? [];

  const [newText, setNewText] = useState("");
  const [newTargetDate, setNewTargetDate] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editTargetDate, setEditTargetDate] = useState("");

  const addGoal = useMutation({
    mutationFn: (input: { text: string; targetDate: string | null }) =>
      apiPost<GoalRow>(`/clients/${clientId}/goals`, { text: input.text, targetDate: input.targetDate }),
    // Appends the row the server actually returned rather than invalidating and refetching: the new goal
    // shows up immediately, and it can't disappear again if the list read lags the write.
    onSuccess: (created) => {
      setNewText("");
      setNewTargetDate("");
      queryClient.setQueryData<GoalRow[]>(queryKeys.goals(clientId), (gs) => [...(gs ?? []), created]);
    },
  });

  const updateGoal = useMutation({
    mutationFn: (input: { goalId: string; text?: string; targetDate?: string | null; achieved?: boolean }) =>
      apiPatch<GoalRow>(`/goals/${input.goalId}`, {
        ...(input.text !== undefined ? { text: input.text } : {}),
        ...(input.targetDate !== undefined ? { targetDate: input.targetDate } : {}),
        ...(input.achieved !== undefined ? { achieved: input.achieved } : {}),
      }),
    // Optimistic, same as the old checkbox was, so marking a goal achieved responds instantly rather
    // than after a round trip -- with the previous list captured so a failure rolls back to exactly what
    // was on screen before. Reconciled with the server's row on success rather than trusted outright,
    // since `achieved_at` is a timestamp the server stamps, not one the client can know in advance.
    onMutate: async (input) => {
      const key = queryKeys.goals(clientId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GoalRow[]>(key);
      queryClient.setQueryData<GoalRow[]>(key, (gs) =>
        gs?.map((g) =>
          g.id === input.goalId
            ? {
                ...g,
                ...(input.text !== undefined ? { text: input.text } : {}),
                ...(input.targetDate !== undefined ? { target_date: input.targetDate } : {}),
                ...(input.achieved !== undefined
                  ? { achieved_at: input.achieved ? new Date().toISOString() : null }
                  : {}),
              }
            : g
        )
      );
      return { previous, key };
    },
    onError: (_err, _vars, context) => {
      if (context) queryClient.setQueryData(context.key, context.previous);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<GoalRow[]>(queryKeys.goals(clientId), (gs) =>
        gs?.map((g) => (g.id === updated.id ? updated : g))
      );
    },
  });

  function startEdit(goal: GoalRow) {
    setEditingId(goal.id);
    setEditText(goal.text);
    setEditTargetDate(goal.target_date ?? "");
  }

  function saveEdit(goalId: string) {
    if (!editText.trim()) return;
    updateGoal.mutate({ goalId, text: editText.trim(), targetDate: editTargetDate || null });
    setEditingId(null);
  }

  if (goalsQuery.isPending) return <LoadingState label="Loading goals…" />;
  if (goalsQuery.isError) return <ErrorState error={goalsQuery.error} onRetry={() => goalsQuery.refetch()} />;

  return (
    <div className="client-goals">
      {goals.length === 0 ? (
        <p className="empty-state">No goals yet.</p>
      ) : (
        <ul className="goal-list">
          {goals.map((goal) =>
            editingId === goal.id ? (
              <li key={goal.id} className="goal-edit">
                <input aria-label="Edit goal text" value={editText} onChange={(e) => setEditText(e.target.value)} />
                <input
                  aria-label="Edit target date"
                  type="date"
                  value={editTargetDate}
                  onChange={(e) => setEditTargetDate(e.target.value)}
                />
                <button type="button" disabled={!editText.trim()} onClick={() => saveEdit(goal.id)}>
                  Save
                </button>
                <button type="button" className="chip" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
              </li>
            ) : (
              <li key={goal.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(goal.achieved_at)}
                    disabled={!canWrite}
                    onChange={() => updateGoal.mutate({ goalId: goal.id, achieved: !goal.achieved_at })}
                  />
                  {goal.text}
                </label>
                {goal.target_date && <span className="badge">Target: {formatTargetDate(goal.target_date)}</span>}
                {goal.achieved_at && (
                  <span className="badge badge-published">Achieved {formatDate(goal.achieved_at)}</span>
                )}
                {canWrite && (
                  <button type="button" className="chip" onClick={() => startEdit(goal)}>
                    Edit
                  </button>
                )}
              </li>
            )
          )}
        </ul>
      )}

      {canWrite && (
        <div className="goal-add">
          <input
            aria-label="New goal"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Add a goal"
          />
          <input
            aria-label="Target date"
            type="date"
            value={newTargetDate}
            onChange={(e) => setNewTargetDate(e.target.value)}
          />
          {/* Disabled while blank and while in flight -- the input is cleared in `onSuccess`, so a second
              click before that landed used to post the identical goal again (UX review pattern, same fix
              already applied here). */}
          <button
            type="button"
            disabled={!newText.trim() || addGoal.isPending}
            onClick={() => addGoal.mutate({ text: newText.trim(), targetDate: newTargetDate || null })}
          >
            {addGoal.isPending ? "Adding…" : "Add goal"}
          </button>
        </div>
      )}
    </div>
  );
}
